import { useAdminUsersAtMikatanoID } from "@/libs/hooks/useAdminUsersAtMikatanoID";
import { useAlerts } from "@/libs/hooks/useAlerts";
import { useBillingBankServices } from "@/libs/hooks/useBillingBankServices";
import { useCompany } from "@/libs/hooks/useCompany";
import { useCompanyInfo } from "@/libs/hooks/useCompanyInfo";
import { useNow } from "@/libs/hooks/useNow";
import { PageTitle } from "../molecules";
import { MainTemplate } from "../templates";
import { Tabs, useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { useCallback, useContext, useState } from "react";
import { css } from "@emotion/react";
import {
  ContractDetail,
  ContractAdmin,
  InvoiceDetail,
  PlanCancel,
  PlanStopCancel,
} from "../organisms";
import { useIsSpMode } from "@/hooks";
import {
  useAddAdminMutation,
  useDeleteAdminMutation,
} from "@/generated/graphql_billing";
import { ErrorCode, moveToErrorPage } from "@/libs/errors";
import { AuthContext } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { useRouter } from "next/router";
import config from "@/libs/config";
import useRollbarUtil from "../hooks/useRollbarUtil";

export const TAB = {
  invoice: {
    index: 0,
    label: "請求情報",
  },
  contract: {
    index: 1,
    label: "契約内容",
  },
  manager: {
    index: 2,
    label: "契約管理者",
  },
};

export const ContractManagementPage = () => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const isSpMode = useIsSpMode();
  const { userInfo } = useContext(AuthContext);
  const userSub = userInfo.sub;
  const router = useRouter();
  const tabIndex = router.query.tab as string;
  const [activeTab, setActiveTab] = useState<number>(TAB.invoice.index);
  const handleTab = (index: number) => {
    setActiveTab(index);
  };

  const [isCancelPage, setIsCanecelPage] = useState<boolean>(false);
  const { notify } = useSnackBar();

  const {
    adminUsersAtMikatanoID,
    adminUsersAtMikatanoIDLoading,
    adminUsersAtMikatanoIDRefetch,
  } = useAdminUsersAtMikatanoID();
  const { alerts, alertsLoading, alertsRefetch } = useAlerts();
  const {
    optionIDs: optionIds,
    billingBankServicesLoading,
    billingBankServicesRefetch,
  } = useBillingBankServices();
  const { company, companyLoading, companyRefetch, isAdmin } = useCompany();
  const { companyInfo, companyInfoLoading, companyInfoRefetch } =
    useCompanyInfo();
  const { now, nowLoading, nowRefetch } = useNow();

  useEffect(() => {
    const fetchData = async () => {
      await companyRefetch();

      await Promise.all([
        adminUsersAtMikatanoIDRefetch(),
        alertsRefetch(),
        billingBankServicesRefetch(),
        companyInfoRefetch(),
        nowRefetch(),
      ]);
    };
    fetchData().catch((e) => {
      console.error("error refetching data", e);
    });
  }, [isCancelPage, activeTab]);

  const [deleteAdmin, { loading: deleteAdminLoading }] = useDeleteAdminMutation(
    {
      onCompleted: async (data) => {
        if (data.deleteAdmin.result) {
          await companyRefetch();
          notify("契約管理者を削除しました");
        }
      },
      onError: getNotifyErrorHandlerForGraphQL("Failed to delete admin user"),
      context: {
        api: "billing",
      },
    }
  );

  const handleDeleteAdmin = useCallback(
    async (userSub: string) => {
      await deleteAdmin({
        variables: {
          input: { userSub },
        },
      });
    },
    [deleteAdmin]
  );

  const [addAdmin, { loading: addAdminLoading }] = useAddAdminMutation({
    onCompleted: async (data) => {
      if (data.addAdmin.result) {
        await companyRefetch();
        notify("契約管理者を追加しました");
      }
    },
    onError: getNotifyErrorHandlerForGraphQL("Failed to add admin user"),
    context: {
      api: "billing",
    },
  });

  const handleAddAdmin = useCallback(
    async (userSub: string) => {
      await addAdmin({
        variables: {
          input: { userSub },
        },
      });
    },
    [addAdmin]
  );

  useEffect(() => {
    if (tabIndex) {
      setActiveTab(Number(tabIndex));
    } else {
      setActiveTab(TAB.invoice.index);
    }
  }, [tabIndex]);

  useEffect(() => {
    if (document.getElementById("root")) {
      document.getElementById("root").scrollIntoView();
    }
  }, [isCancelPage, activeTab]);

  if (
    addAdminLoading ||
    adminUsersAtMikatanoIDLoading ||
    alertsLoading ||
    billingBankServicesLoading ||
    companyInfoLoading ||
    companyLoading ||
    deleteAdminLoading ||
    nowLoading
  ) {
    return <div>Loading...</div>;
  }

  if (!isAdmin) {
    moveToErrorPage(ErrorCode.ONLY_ADMINISTRATOR_CAN_OPERATE_IT);
  }

  const title = `${config.BANK_NICKNAME} Mikatano シリーズ サービス一覧`;
  if (isCancelPage) {
    return (
      <MainTemplate title={title}>
        {company.planContract ? (
          <PlanCancel
            now={now}
            company={company}
            setIsCanecelPage={setIsCanecelPage}
          />
        ) : (
          <PlanStopCancel
            company={company}
            onClickCancel={() => setIsCanecelPage(false)}
            setIsCanecelPage={setIsCanecelPage}
            now={now}
          />
        )}
      </MainTemplate>
    );
  }
  return (
    <MainTemplate title={title}>
      <PageTitle mainTitle={"契約管理"} subTitle="" />
      <div css={container(isSpMode)}>
        <Tabs
          items={[
            { label: TAB.invoice.label },
            { label: TAB.contract.label },
            { label: TAB.manager.label },
          ]}
          onClick={handleTab}
          activeTabIndex={activeTab}
        />
        {activeTab === TAB.invoice.index && (
          <InvoiceDetail
            company={company}
            setActiveTab={setActiveTab}
            alerts={alerts}
            now={now}
            companyInfo={companyInfo}
          />
        )}
        {activeTab === TAB.contract.index && (
          <ContractDetail
            setIsCanecelPage={setIsCanecelPage}
            company={company}
            optionIds={optionIds}
            alerts={alerts}
            now={now}
          />
        )}
        {activeTab === TAB.manager.index && (
          <ContractAdmin
            company={company}
            userSub={userSub}
            handleDeleteAdmin={handleDeleteAdmin}
            handleAddAdmin={handleAddAdmin}
            adminUsersAtMikatanoID={adminUsersAtMikatanoID}
          />
        )}
      </div>
    </MainTemplate>
  );
};

const container = (isSpMode: boolean) =>
  css({
    width: isSpMode ? "auto" : "640px",
    margin: "0px auto",
  });
